import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, X, PlusCircle, Check, ShoppingCart, Plus, Minus,
  Trash2, ChevronRight, ChevronDown, ArrowLeft, Image as ImageIcon
} from 'lucide-react';
import StepIndicator from './StepIndicator';

const SingleMealSelectionStyles = () => (
  <style>{`
    :root {
      --primary: #2F7D5E;
      --primary-dark: #245F48;
      --accent: #f4f4f4;
      --border: #e8e8e8;
      --text-main: #1a1a1a;
      --text-sub: #666;
      --danger: #e53e3e;
    }

    .single-meal-page {
      display: grid;
      grid-template-columns: 1fr 340px;
      gap: 24px;
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 clamp(12px, 3vw, 20px);
      box-sizing: border-box;
      font-family: 'Plus Jakarta Sans', 'Noto Sans TC', sans-serif;
    }
    .single-meal-page.embedded {
      max-width: none;
      padding: 0;
      margin: 0;
    }

    /* Left: Menu browsing */
    .menu-browse {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .menu-search-bar {
      position: relative;
    }
    .menu-search-bar input {
      width: 100%;
      padding: 14px 16px 14px 46px;
      border-radius: 4px;
      border: 1px solid var(--border);
      font-size: 1rem;
      background: #fff;
      transition: all 0.2s;
      box-sizing: border-box;
      font-family: inherit;
    }
    .menu-search-bar input:focus {
      outline: none;
      border-color: var(--primary);
      box-shadow: 0 0 0 3px rgba(47,125,94,0.15);
    }
    .menu-search-icon {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
    }

    .category-tabs {
      display: flex;
      gap: 8px;
      overflow-x: auto;
      padding-bottom: 4px;
      -webkit-overflow-scrolling: touch;
    }
    .category-tabs::-webkit-scrollbar { height: 0; }
    .cat-tab {
      padding: 10px 20px;
      border: none;
      border-radius: 4px;
      background: transparent;
      font-size: 0.9rem;
      font-weight: 400;
      color: var(--text-sub);
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.2s;
      font-family: inherit;
    }
    .cat-tab:hover {
      background: #eee;
      color: var(--text-main);
    }
    .cat-tab.active {
      background: var(--primary);
      color: #fff;
    }

    .category-section-title {
      font-size: 1.15rem;
      font-weight: 600;
      color: var(--text-main);
      padding-bottom: 8px;
      border-bottom: 2px solid var(--primary);
      margin-bottom: 4px;
      margin-top: 12px;
    }
    .category-section + .category-section {
      margin-top: 32px;
    }

    .dishes-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 20px;
    }
    .dish-card {
      border: 1px solid #eee;
      border-radius: 4px;
      padding: 14px;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      flex-direction: column;
      background: #fff;
    }
    .dish-card:hover {
      border-color: var(--primary);
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(0,0,0,0.06);
    }
    .dish-img-wrap {
      position: relative;
      margin-bottom: 12px;
    }
    .dish-img {
      width: 100%;
      height: 150px;
      background: #f0f0f0;
      border-radius: 4px;
      object-fit: cover;
      display: block;
    }
    .dish-img-placeholder {
      width: 100%;
      height: 150px;
      background: #f8f8f8;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ccc;
      border: 1px dashed #ddd;
    }
    .add-icon {
      position: absolute;
      bottom: 6px;
      right: 6px;
      width: 32px;
      height: 32px;
      background: rgba(255,255,255,0.95);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--primary);
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      transition: all 0.2s;
      z-index: 1;
    }
    .dish-card:hover .add-icon {
      background: var(--primary);
      color: #fff;
      box-shadow: 0 2px 12px rgba(47,125,94,0.3);
    }

    /* 售罄樣式 */
    .dish-card.sold-out {
      opacity: 0.6;
      pointer-events: none;
      position: relative;
    }
    .dish-card.sold-out::after {
      content: '售罄';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0,0,0,0.7);
      color: white;
      padding: 8px 16px;
      border-radius: 4px;
      font-weight: 400;
      font-size: 1rem;
      z-index: 10;
    }
    .dish-card.sold-out .add-icon {
      display: none;
    }

    .dish-text {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }
    .dish-name {
      font-weight: 400;
      margin-bottom: 4px;
      color: var(--text-main);
      font-size: 1rem;
      line-height: 1.3;
    }
    .dish-name-en {
      font-size: 0.85rem;
      color: #999;
      margin-bottom: 6px;
    }
    .dish-desc {
      font-size: 0.85rem;
      color: #888;
      margin-bottom: 12px;
      flex: 1;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .dish-footer {
      display: flex;
      align-items: center;
      margin-top: auto;
    }
    .dish-price {
      font-weight: 400;
      color: var(--primary);
      font-size: 1.1rem;
    }

    /* Right: Cart sidebar */
    .cart-sidebar {
      position: sticky;
      top: 20px;
      border: 1px solid #e2e8f0;
      border-radius: 4px;
      background: #fff;
      height: fit-content;
      max-height: calc(100vh - 120px);
      box-shadow: 0 4px 20px rgba(0,0,0,0.04);
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .cart-header {
      padding: 20px 24px;
      border-bottom: 1px solid #f1f5f9;
      font-size: 1.1rem;
      font-weight: 400;
      display: flex;
      align-items: center;
      gap: 10px;
      color: var(--primary);
    }
    .cart-badge {
      background: var(--primary);
      color: #fff;
      font-size: 0.75rem;
      font-weight: 400;
      padding: 2px 8px;
      border-radius: 4px;
      min-width: 20px;
      text-align: center;
    }

    .cart-items-scroll {
      flex: 1;
      overflow-y: auto;
      padding: 0;
      max-height: calc(100vh - 450px);
    }

    .cart-item {
      display: flex;
      gap: 12px;
      padding: 16px 24px;
      border-bottom: 1px solid #f5f5f5;
      align-items: flex-start;
    }
    .cart-item:last-child { border-bottom: none; }
    .cart-item-img {
      width: 50px;
      height: 50px;
      border-radius: 4px;
      object-fit: cover;
      flex-shrink: 0;
      background: #f1f5f9;
    }
    .cart-item-info {
      flex: 1;
      min-width: 0;
    }
    .cart-item-name {
      font-weight: 400;
      font-size: 0.85rem;
      color: var(--text-main);
      margin-bottom: 2px;
    }
    .cart-item-mods {
      font-size: 0.78rem;
      color: var(--text-sub);
      margin-bottom: 6px;
    }
    .cart-mod-line {
      line-height: 1.5;
    }
    .cart-item-bottom {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .cart-item-price {
      font-weight: 400;
      font-size: 0.85rem;
      color: var(--text-main);
    }

    /* 購物車售罄警告 */
    .cart-item.sold-out {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 4px;
      margin: 0 -8px;
      padding-left: 16px;
      padding-right: 16px;
    }
    .cart-item-soldout-badge {
      background: #ef4444;
      color: white;
      font-size: 0.7rem;
      padding: 2px 6px;
      border-radius: 4px;
      font-weight: 400;
      margin-left: 6px;
    }
    .cart-item.sold-out .cart-item-name {
      color: #991b1b;
    }
    /* 售罄商品只禁用增加按鈕，允許移除 */
    .cart-item.sold-out .qty-btn:last-child {
      opacity: 0.3;
      pointer-events: none;
    }

    .qty-control {
      display: flex;
      align-items: center;
      gap: 0;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      overflow: hidden;
    }
    .qty-btn {
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: #fff;
      cursor: pointer;
      color: var(--text-sub);
      transition: background 0.15s;
      font-family: inherit;
    }
    .qty-btn:hover { background: #f5f5f5; }
    .qty-btn.delete-btn:hover { background: #fff0f0; color: var(--danger); }
    .qty-num {
      width: 30px;
      text-align: center;
      font-weight: 400;
      font-size: 0.85rem;
      border-left: 1px solid #e0e0e0;
      border-right: 1px solid #e0e0e0;
      line-height: 30px;
    }

    .cart-empty {
      padding: 40px 24px;
      text-align: center;
      color: #999;
      font-size: 0.9rem;
    }

    .cutlery-toggle {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 24px;
      border-top: 1px solid #e2e8f0;
      font-size: 0.9rem;
      color: #334155;
      cursor: pointer;
    }
    .cutlery-toggle input {
      width: 18px; height: 18px;
      accent-color: #2F7D5E;
      cursor: pointer;
    }
    .cart-totals {
      padding: 20px 24px;
      border-top: 1px solid #e2e8f0;
      background: #fff;
    }
    .cart-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
      font-size: 0.9rem;
      color: #555;
    }
    .cart-row.fee-row {
      margin-bottom: 12px;
      padding-bottom: 12px;
      border-bottom: 1px dashed #e2e8f0;
    }
    .cart-row.tax-row { font-size: 0.85rem; color: #94a3b8; }
    .cart-total-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #e2e8f0;
      font-weight: 400;
      color: var(--primary);
    }
    .cart-total-label { font-size: 1rem; }
    .cart-total-amount { font-size: 1.3rem; }

    .cart-actions {
      padding: 20px 24px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .checkout-btn {
      width: 100%;
      padding: 16px;
      border-radius: 4px;
      background: var(--primary);
      color: #fff;
      border: none;
      font-weight: 400;
      font-size: 1.05rem;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      transition: all 0.2s;
      box-shadow: 0 4px 12px rgba(47,125,94,0.2);
      font-family: inherit;
    }
    .checkout-btn:hover { background: var(--primary-dark); }
    .checkout-btn:disabled { background: #cbd5e1; cursor: not-allowed; box-shadow: none; }
    .back-btn-cart {
      width: 100%;
      padding: 14px;
      border-radius: 4px;
      background: #fff;
      color: #666;
      border: 1px solid #ddd;
      font-weight: 400;
      font-size: 0.95rem;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 6px;
      transition: all 0.2s;
      font-family: inherit;
    }
    .back-btn-cart:hover { background: #f9f9f9; border-color: #ccc; color: #333; }

    /* Modifier Modal */
    .mod-overlay {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: #fff;
      z-index: 2000;
      display: flex;
      flex-direction: column;
      animation: fadeIn 0.2s ease;
    }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    .mod-modal {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      max-width: 900px;
      margin: 0 auto;
    }
    .mod-modal-header {
      padding: 12px 16px;
      border-bottom: 1px solid #f0f0f0;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .mod-back-btn {
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 400;
      color: var(--text-sub);
      padding: 8px 12px;
      border-radius: 4px;
      transition: background 0.2s;
      font-size: 1rem;
      font-family: inherit;
    }
    .mod-back-btn:hover { background: #f5f5f5; }
    .mod-modal-body {
      flex: 1;
      overflow-y: auto;
      padding: 40px;
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
    .mod-modal-body::-webkit-scrollbar { display: none; }
    .mod-dish-info {
      margin-bottom: 30px;
      padding-bottom: 24px;
      border-bottom: 1px solid #f0f0f0;
    }
    .mod-dish-img {
      width: 230px;
      height: 230px;
      border-radius: 4px;
      object-fit: cover;
      margin: 0 auto 16px;
      display: block;
    }
    .mod-dish-detail {}
    .mod-dish-name { font-size: 1.8rem; font-weight: 400; color: var(--primary); margin-bottom: 6px; }
    .mod-dish-price-label { font-size: 1.2rem; color: var(--text-sub); margin-bottom: 8px; }
    .mod-dish-desc { font-size: 0.9rem; color: #888; line-height: 1.5; }

    .mod-group {
      margin-bottom: 30px;
      border: none;
      border-radius: 0;
      padding: 0;
      border-bottom: 1px solid #f0f0f0;
      padding-bottom: 16px;
    }
    .mod-group-head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0;
      padding-bottom: 10px;
      border-bottom: 1px solid #eee;
    }
    .mod-group-name { font-weight: 400; font-size: 1rem; color: var(--text-main); }
    .mod-rules-badge {
      font-size: 0.75rem;
      padding: 4px 10px;
      border-radius: 4px;
      font-weight: 400;
    }
    .badge-required { background: #f0faf5; color: #2F7D5E; border: 1px solid #d1f0e2; }
    .badge-optional { background: #f0faf5; color: #2F7D5E; border: 1px solid #d1f0e2; }
    .mod-options {
      display: flex;
      flex-direction: column;
      gap: 0;
    }
    .mod-opt {
      border: none;
      border-radius: 0;
      padding: 14px 0;
      border-bottom: 1px solid #f5f5f5;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      justify-content: space-between;
      align-items: center;
      user-select: none;
    }
    .mod-opt:last-child { border-bottom: none; }
    .mod-opt:hover { background: transparent; }
    .mod-opt.selected { background: transparent; }
    .mod-opt-left { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
    .mod-opt-name { font-weight: 400; font-size: 0.9rem; }
    .mod-opt-price { font-size: 0.85rem; color: var(--text-sub); font-weight: 400; }
    .mod-opt.selected .mod-opt-price { color: var(--primary); }
    .mod-opt-radio {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 1px solid #d0d0d0;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: all 0.2s;
    }
    .mod-opt:hover .mod-opt-radio { border-color: #aaa; }
    .mod-opt.selected .mod-opt-radio {
      border-color: var(--primary);
      background: var(--primary);
    }
    .mod-opt-radio-inner {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #fff;
      opacity: 0;
      transform: scale(0);
      transition: all 0.2s;
    }
    .mod-opt.selected .mod-opt-radio-inner {
      opacity: 1;
      transform: scale(1);
    }

    .mod-note-input {
      width: 100%;
      padding: 12px 14px;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      font-size: 0.9rem;
      font-family: inherit;
      resize: vertical;
      min-height: 60px;
      box-sizing: border-box;
      transition: border-color 0.2s;
    }
    .mod-note-input:focus {
      outline: none;
      border-color: var(--primary);
      box-shadow: 0 0 0 3px rgba(47,125,94,0.12);
    }
    .mod-note-input::placeholder { color: #bbb; }
    .mod-note-section {
      margin-bottom: 24px;
    }
    .mod-note-label {
      font-weight: 400;
      font-size: 0.9rem;
      color: var(--text-main);
      margin-bottom: 10px;
      display: block;
    }
    .cart-item-note {
      font-size: 0.78rem;
      color: #b45309;
      margin-top: 2px;
      font-style: italic;
    }

    .mod-modal-footer {
      padding: 24px 40px;
      border-top: 1px solid var(--border);
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #fff;
      box-shadow: 0 -4px 20px rgba(0,0,0,0.03);
    }
    .mod-total-wrapper { display: flex; flex-direction: column; }
    .mod-total-label { font-size: 0.9rem; color: #888; margin-bottom: 4px; }
    .mod-total-amount { font-size: 1.6rem; font-weight: 400; color: var(--primary); }
    .mod-confirm-btn {
      background: var(--primary);
      color: #fff;
      padding: 16px 48px;
      border-radius: 4px;
      border: none;
      font-weight: 400;
      font-size: 1.1rem;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 10px;
      font-family: inherit;
    }
    .mod-confirm-btn:hover { background: var(--primary-dark); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(47,125,94,0.2); }

    .no-results {
      text-align: center;
      padding: 40px;
      color: #999;
    }

    /* ===== Mobile sticky cart bar + drawer ===== */
    .mobile-cart-bar {
      display: none;
    }
    @media (max-width: 900px) {
      .mobile-cart-bar {
        display: flex;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1001;
        background: #fff;
        border-top: 1px solid #e2e8f0;
        box-shadow: 0 -4px 20px rgba(0,0,0,0.08);
        padding: 12px 16px;
        align-items: center;
        gap: 12px;
      }
      .mobile-cart-bar-info {
        flex: 1;
        min-width: 0;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .mobile-cart-bar-badge {
        width: 40px; height: 40px;
        border-radius: 50%;
        background: var(--primary);
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        position: relative;
      }
      .mobile-cart-bar-badge-count {
        position: absolute;
        top: -4px; right: -4px;
        background: #ef4444;
        color: #fff;
        font-size: 0.65rem;
        font-weight: 400;
        min-width: 18px;
        height: 18px;
        border-radius: 9px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 4px;
      }
      .mobile-cart-bar-text {
        flex: 1;
        min-width: 0;
      }
      .mobile-cart-bar-label {
        font-size: 0.78rem;
        color: #64748b;
      }
      .mobile-cart-bar-total {
        font-size: 1.05rem;
        font-weight: 400;
        color: #1e293b;
      }
      .mobile-cart-bar-arrow {
        color: #94a3b8;
        transition: transform 0.3s;
        flex-shrink: 0;
      }
      .mobile-cart-bar-arrow.open {
        transform: rotate(180deg);
      }
      .mobile-cart-bar-btn {
        padding: 14px 24px;
        border-radius: 4px;
        background: var(--primary);
        color: #fff;
        border: none;
        font-weight: 400;
        font-size: 0.95rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        white-space: nowrap;
        box-shadow: 0 4px 12px rgba(47,125,94,0.2);
        font-family: inherit;
        transition: background 0.2s;
      }
      .mobile-cart-bar-btn:hover { background: var(--primary-dark); }

      /* Drawer overlay */
      .mobile-cart-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.4);
        z-index: 1000;
        animation: mcFadeIn 0.2s ease;
      }
      @keyframes mcFadeIn { from { opacity: 0; } to { opacity: 1; } }

      /* Drawer panel */
      .mobile-cart-drawer {
        position: fixed;
        bottom: 68px;
        left: 0;
        right: 0;
        z-index: 1001;
        background: #fff;
        border-radius: 4px 4px 0 0;
        max-height: 55vh;
        display: flex;
        flex-direction: column;
        animation: mcSlideUp 0.3s ease;
        box-shadow: 0 -8px 30px rgba(0,0,0,0.12);
      }
      @keyframes mcSlideUp {
        from { transform: translateY(100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      .mobile-cart-drawer-handle {
        width: 36px; height: 4px;
        background: #ddd; border-radius: 2px;
        margin: 10px auto 0;
      }
      .mobile-cart-drawer-header {
        padding: 12px 20px 10px;
        font-size: 1rem;
        font-weight: 400;
        color: #1e293b;
        border-bottom: 1px solid #f1f5f9;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .mobile-cart-drawer-items {
        flex: 1;
        overflow-y: auto;
        padding: 0;
      }
      /* Reuse existing cart-item styles inside drawer */
      .mobile-cart-drawer-items .cart-item {
        padding: 12px 20px;
      }
      .mobile-cart-drawer-totals {
        padding: 14px 20px;
        border-top: 1px solid #e2e8f0;
        background: #fafafa;
      }

      /* Add bottom padding to page so content isn't hidden behind the bar */
      .single-meal-page {
        padding-bottom: 80px;
      }
    }

    /* ===== Tablet / Mobile: single column layout ===== */
    @media (max-width: 900px) {
      .single-meal-page {
        grid-template-columns: 1fr;
        gap: 0;
        padding: 0;
      }
      .menu-browse { order: 1; min-width: 0; gap: 0; }
      .menu-search-bar { margin-bottom: 8px; }
      .category-tabs {
        padding: 6px 0;
        border-bottom: 1px solid var(--border);
        gap: 6px;
        margin-bottom: 0;
      }
      .cat-tab { padding: 6px 14px; font-size: 0.85rem; }
      .cart-sidebar {
        display: none;
      }

      /* Dish cards — horizontal row: text left, image right (match weekly) */
      .dishes-grid {
        grid-template-columns: 1fr;
        gap: 0;
        padding: 0;
      }
      .dish-card {
        flex-direction: row;
        gap: 12px;
        align-items: center;
        padding: 12px 0;
        border-radius: 0;
        border: none;
        border-bottom: 1px solid #f0f0f0;
      }
      .dish-card:hover { transform: none; box-shadow: none; border-color: #f0f0f0; }
      .dish-text { order: 1; flex: 1; min-width: 0; }
      .dish-img-wrap {
        order: 2;
        flex-shrink: 0;
        margin-bottom: 0;
      }
      .dish-card.has-img .dish-img-wrap {
        width: 100px;
        height: 100px;
      }
      .dish-card.has-img .dish-img {
        width: 100%;
        height: 100%;
        border-radius: 4px;
      }
      .dish-card.no-img .dish-img-wrap { display: none; }
      .dish-img-placeholder { display: none; }
      .add-icon { display: none; }
      .dish-desc { font-size: 0.78rem; margin-bottom: 4px; -webkit-line-clamp: 1; }
      .dish-name { font-size: 0.9rem; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .dish-name-en { font-size: 0.78rem; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .dish-footer { margin-top: 4px; }
      .dish-price { font-size: 0.95rem; }
    }

    /* ===== Phone: tighter spacing ===== */
    @media (max-width: 640px) {
      .single-meal-page { gap: 14px; }

      .menu-search-bar input {
        padding: 12px 14px 12px 40px;
        font-size: 0.9rem;
        border-radius: 4px;
      }
      .menu-search-icon { left: 14px; }

      .dish-card { padding: 10px 0; gap: 10px; }
      .dish-card.has-img .dish-img-wrap { width: 80px; height: 80px; }

      /* Cart — compact */
      .cart-sidebar { border-radius: 4px; }
      .cart-header { padding: 14px 16px; font-size: 1rem; }
      .cart-items-scroll { max-height: 220px; }
      .cart-item { padding: 12px 16px; gap: 10px; }
      .cart-item-img { width: 40px; height: 40px; border-radius: 4px; }
      .cart-item-name { font-size: 0.8rem; }
      .cart-item-mods { font-size: 0.72rem; }
      .cart-item-price { font-size: 0.8rem; }
      .qty-btn { width: 28px; height: 28px; }
      .qty-num { width: 28px; font-size: 0.8rem; line-height: 28px; }

      .cart-totals { padding: 14px 16px; }
      .cart-row { font-size: 0.85rem; margin-bottom: 6px; }
      .cart-row.tax-row { font-size: 0.8rem; }
      .cart-total-row { margin-top: 10px; padding-top: 10px; }
      .cart-total-label { font-size: 0.9rem; }
      .cart-total-amount { font-size: 1.15rem; }

      .cart-actions { padding: 14px 16px; gap: 8px; }
      .checkout-btn { padding: 14px; font-size: 0.95rem; border-radius: 4px; }
      .back-btn-cart { padding: 12px; font-size: 0.85rem; border-radius: 4px; }

      /* Modifier fullscreen */
      .mod-modal-body { padding: 16px; }
      .mod-dish-info { margin-bottom: 16px; padding-bottom: 14px; text-align: center; }
      .mod-dish-img { width: 140px; height: 140px; border-radius: 4px; }
      .mod-dish-name { font-size: 1.2rem; text-align: center; }
      .mod-dish-price-label { font-size: 1rem; text-align: center; }
      .mod-dish-desc { text-align: center; }
      .mod-opt-name { font-size: 0.9rem; }
      .mod-opt-price { font-size: 0.85rem; }
      .mod-modal-footer { padding: 16px; }
      .mod-confirm-btn { padding: 12px 24px; font-size: 1rem; }
    }
  `}</style>
);

const SingleMealSelection = ({ embedded = false }) => {
  const { t, i18n } = useTranslation('booking');
  const isZh = i18n.language?.startsWith('zh');
  const navigate = useNavigate();
  const {
    menuOptions, categoriesList, modifierGroupsList,
    bookingMode, matchedZone, orderType,
    cartItems, addToCart, updateCartItemQuantity,
    handleMealSelectionNext,
    isLoggedIn, requestLogin,
    globalDiscount,
    needCutlery, setNeedCutlery,
  } = useOutletContext();

  const discountEnabled = globalDiscount?.enabled && globalDiscount?.rate && globalDiscount.rate < 1;
  const discountRate = discountEnabled ? globalDiscount.rate : 1;

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [modModalDish, setModModalDish] = useState(null);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [itemNote, setItemNote] = useState('');

  // No need to auto-select first category; default '' means "All"

  const filteredMenu = useMemo(() => {
    let items = menuOptions || [];
    if (selectedCategoryId) {
      items = items.filter(item =>
        Array.isArray(item.categories) && item.categories.includes(selectedCategoryId)
      );
    }
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      items = items.filter(m =>
        m.name?.toLowerCase().includes(lower) ||
        m.description?.toLowerCase().includes(lower) ||
        m.alternate_name?.toLowerCase().includes(lower)
      );
    }
    return items;
  }, [menuOptions, selectedCategoryId, searchTerm]);

  // Group items by category for "All" view
  const groupedByCategory = useMemo(() => {
    if (selectedCategoryId || !categoriesList?.length) return null;
    const items = searchTerm
      ? (menuOptions || []).filter(m => {
          const lower = searchTerm.toLowerCase();
          return m.name?.toLowerCase().includes(lower) ||
            m.description?.toLowerCase().includes(lower) ||
            m.alternate_name?.toLowerCase().includes(lower);
        })
      : (menuOptions || []);
    return categoriesList
      .map(cat => ({
        ...cat,
        dishes: items.filter(item =>
          Array.isArray(item.categories) && item.categories.includes(cat.id)
        ),
      }))
      .filter(cat => cat.dishes.length > 0);
  }, [selectedCategoryId, categoriesList, menuOptions, searchTerm]);

  const getDishModifierGroups = (dish) => {
    if (!dish?.modifier_groups || !modifierGroupsList) return [];
    return modifierGroupsList
      .filter(g => dish.modifier_groups.includes(g.id))
      .sort((a, b) => (a.order || 999) - (b.order || 999));
  };

  const handleDishClick = (dish) => {
    setModModalDish(dish);
    setSelectedModifiers({});
    setItemNote('');
  };

  // 檢查購物車是否有售罄商品
  const checkCartAvailability = () => {
    const soldOutItems = cartItems.filter(item => {
      const menuItem = menuOptions.find(m => m.id === item.menuItemId);
      return menuItem && menuItem.is_available === false;
    });
    return soldOutItems;
  };

  // 結帳前驗證
  const handleCheckout = () => {
    // 檢查售罄商品
    const soldOutItems = checkCartAvailability();
    if (soldOutItems.length > 0) {
      const names = soldOutItems.map(i => i.name).join('、');
      alert(`以下商品已售罄，請先移除：\n${names}`);
      return;
    }

    // 檢查登入狀態
    if (!isLoggedIn) {
      // 儲存購物車到 localStorage
      localStorage.setItem('pending_cart', JSON.stringify(cartItems));
      localStorage.setItem('pending_order_type', orderType);
      requestLogin();
      return;
    }

    handleMealSelectionNext();
  };

  const toggleModifier = (group, mod) => {
    const currentSelection = selectedModifiers[group.id] || [];
    const isSelected = currentSelection.some(m => m.id === mod.id);
    let newSelection;
    if (isSelected) {
      // 已選中，點擊取消
      newSelection = currentSelection.filter(m => m.id !== mod.id);
    } else if (group.max_allowed === 1) {
      // 單選模式，替換選擇
      newSelection = [mod];
    } else {
      // 多選模式，新增選擇
      if (currentSelection.length >= group.max_allowed) {
        return;
      }
      newSelection = [...currentSelection, mod];
    }
    setSelectedModifiers({ ...selectedModifiers, [group.id]: newSelection });
  };

  const handleConfirmModifiers = () => {
    const groups = getDishModifierGroups(modModalDish);
    for (const group of groups) {
      const selected = selectedModifiers[group.id] || [];
      if (selected.length < group.min_required) {
        alert(t('meal_selection.alert_min_modifier', { name: group.name, min: group.min_required }));
        return;
      }
    }
    const allMods = Object.values(selectedModifiers).flat();
    addToCart(modModalDish, allMods, 1, itemNote.trim());
    setModModalDish(null);
  };

  const calculateModPreviewTotal = () => {
    if (!modModalDish) return 0;
    let total = modModalDish.price;
    Object.values(selectedModifiers).flat().forEach(m => total += (m.price || 0));
    return (total * discountRate).toFixed(2);
  };

  // Cart calculations
  const cartTotals = useMemo(() => {
    let originalSubtotal = 0;
    let subtotal = 0;
    let gst = 0;
    let pst = 0;

    cartItems.forEach(item => {
      const modTotal = item.modifiers.reduce((s, m) => s + (m.price || 0), 0);
      const itemOriginal = (item.price + modTotal) * item.quantity;
      const itemTotal = itemOriginal * discountRate;
      originalSubtotal += itemOriginal;
      subtotal += itemTotal;
      if (item.tax_gst) gst += itemTotal * 0.05;
      if (item.tax_pst) pst += itemTotal * 0.07;
    });

    let deliveryFee = 0;
    if (orderType === 'delivery' && matchedZone) {
      const rawFee = matchedZone.delivery_fee ?? matchedZone.deliveryFee ?? 0;
      deliveryFee = Number(rawFee);
    }

    const discount = discountEnabled ? originalSubtotal - subtotal : 0;

    return {
      originalSubtotal: discountEnabled ? originalSubtotal : subtotal,
      subtotal,
      discount,
      gst,
      pst,
      deliveryFee,
      grandTotal: subtotal + gst + pst + deliveryFee,
    };
  }, [cartItems, matchedZone, orderType, discountRate, discountEnabled]);

  const totalItemCount = cartItems.reduce((s, item) => s + item.quantity, 0);

  return (
    <>
      <SingleMealSelectionStyles />
      {!embedded && <StepIndicator currentStep={1} bookingMode={bookingMode} />}

      {discountEnabled && (
        <div style={{
          background: 'linear-gradient(90deg, #e53e3e, #c53030)',
          color: '#fff',
          textAlign: 'center',
          padding: '10px 16px',
          fontSize: '0.95rem',
          fontWeight: 500,
          borderRadius: 4,
          maxWidth: 1200,
          margin: '0 auto 16px',
          boxSizing: 'border-box',
        }}>
          {globalDiscount.label || t('single_meal.discount', '全單折扣')}
        </div>
      )}

      <div className={`single-meal-page ${embedded ? 'embedded' : ''}`}>
        {/* Left: Menu */}
        <div className="menu-browse">
          <div className="menu-search-bar">
            <Search size={18} className="menu-search-icon" />
            <input
              type="text"
              placeholder={t('meal_selection.search_placeholder')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="category-tabs">
            <button
              className={`cat-tab ${selectedCategoryId === '' ? 'active' : ''}`}
              onClick={() => setSelectedCategoryId('')}
            >
              {t('meal_selection.all_categories', '全部')}
            </button>
            {(categoriesList || []).map(cat => (
              <button
                key={cat.id}
                className={`cat-tab ${selectedCategoryId === cat.id ? 'active' : ''}`}
                onClick={() => setSelectedCategoryId(cat.id)}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {(() => {
            const renderDish = (dish) => (
              <div
                key={dish.id}
                className={`dish-card ${dish.image_url ? 'has-img' : 'no-img'} ${dish.is_available === false ? 'sold-out' : ''}`}
                onClick={() => dish.is_available !== false && handleDishClick(dish)}
              >
                <div className="dish-img-wrap">
                  {dish.image_url ? (
                    <img src={dish.image_url} alt={dish.name} className="dish-img" loading="lazy" decoding="async" />
                  ) : (
                    <div className="dish-img-placeholder"><ImageIcon size={24} /></div>
                  )}
                  <div className="add-icon"><PlusCircle size={20} /></div>
                </div>
                <div className="dish-text">
                  <div className="dish-name">{dish.name}</div>
                  {!isZh && dish.name !== dish.alternate_name && dish.alternate_name && (
                    <div className="dish-name-en">{dish.alternate_name}</div>
                  )}
                  {dish.description && <div className="dish-desc">{dish.description}</div>}
                  <div className="dish-footer">
                    {discountEnabled ? (
                      <div className="dish-price">
                        <span style={{textDecoration:'line-through', color:'#999', fontSize:'0.85em', marginRight:6}}>CAD$ {dish.price}</span>
                        <span style={{color:'#e53e3e', fontWeight:500}}>CAD$ {(dish.price * discountRate).toFixed(2)}</span>
                      </div>
                    ) : (
                      <div className="dish-price">CAD$ {dish.price}</div>
                    )}
                  </div>
                </div>
              </div>
            );

            if (groupedByCategory) {
              return groupedByCategory.length === 0 ? (
                <div className="no-results">{t('meal_selection.no_results')}</div>
              ) : (
                groupedByCategory.map(cat => (
                  <div key={cat.id} className="category-section">
                    <div className="category-section-title">{cat.name}</div>
                    <div className="dishes-grid">
                      {cat.dishes.map(renderDish)}
                    </div>
                  </div>
                ))
              );
            }

            return filteredMenu.length === 0 ? (
              <div className="no-results">{t('meal_selection.no_results')}</div>
            ) : (
              <div className="dishes-grid">
                {filteredMenu.map(renderDish)}
              </div>
            );
          })()}
        </div>

        {/* Right: Cart */}
        <div className="cart-sidebar">
          <div className="cart-header">
            <ShoppingCart size={22} />
            {t('single_meal.cart_title', '購物車')}
            {totalItemCount > 0 && <span className="cart-badge">{totalItemCount}</span>}
          </div>

          <div className="cart-items-scroll">
            {cartItems.length === 0 ? (
              <div className="cart-empty">{t('single_meal.cart_empty', '購物車是空的，點擊菜品加入')}</div>
            ) : (
              cartItems.map(item => {
                const modTotal = item.modifiers.reduce((s, m) => s + (m.price || 0), 0);
                const lineOriginal = (item.price + modTotal) * item.quantity;
                const lineTotal = lineOriginal * discountRate;
                const menuItem = menuOptions.find(m => m.id === item.menuItemId);
                const isSoldOut = menuItem && menuItem.is_available === false;
                return (
                  <div key={item.cartId} className={`cart-item ${isSoldOut ? 'sold-out' : ''}`}>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="cart-item-img" loading="lazy" decoding="async" />
                    ) : (
                      <div className="cart-item-img" style={{display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <ImageIcon size={18} color="#ccc" />
                      </div>
                    )}
                    <div className="cart-item-info">
                      <div className="cart-item-name">
                        {item.name}
                        {isSoldOut && <span className="cart-item-soldout-badge">售罄</span>}
                      </div>
                      {item.modifiers.length > 0 && (
                        <div className="cart-item-mods">
                          {item.modifiers.map((m, i) => (
                            <div key={i} className="cart-mod-line">
                              {m.name}
                              {m.price > 0 && (
                                discountEnabled ? (
                                  <span style={{color:'#e53e3e', fontWeight:600}}> (+${(Number(m.price) * discountRate).toFixed(2)})</span>
                                ) : (
                                  <span style={{color:'#ef4444', fontWeight:600}}> (+${Number(m.price).toFixed(2)})</span>
                                )
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {item.note && (
                        <div className="cart-item-note">{t('single_meal.note_label', '備註')}: {item.note}</div>
                      )}
                      <div className="cart-item-bottom">
                        <div className="cart-item-price">
                          {discountEnabled && <span style={{textDecoration:'line-through', color:'#999', fontSize:'0.85em', marginRight:4}}>${lineOriginal.toFixed(2)}</span>}
                          ${lineTotal.toFixed(2)}
                        </div>
                        <div className="qty-control">
                          <button
                            className={`qty-btn ${item.quantity === 1 ? 'delete-btn' : ''}`}
                            onClick={() => updateCartItemQuantity(item.cartId, item.quantity - 1)}
                          >
                            {item.quantity === 1 ? <Trash2 size={14} /> : <Minus size={14} />}
                          </button>
                          <span className="qty-num">{item.quantity}</span>
                          <button
                            className="qty-btn"
                            onClick={() => updateCartItemQuantity(item.cartId, item.quantity + 1)}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {cartItems.length > 0 && (
            <label className="cutlery-toggle">
              <span>{t('single_meal.need_cutlery', '是否需要餐具')}</span>
              <input
                type="checkbox"
                checked={needCutlery}
                onChange={() => setNeedCutlery(!needCutlery)}
              />
            </label>
          )}

          {cartItems.length > 0 && (
            <div className="cart-totals">
              {discountEnabled && (
                <>
                  <div className="cart-row">
                    <span>{t('meal_selection.subtotal')}</span>
                    <span style={{textDecoration:'line-through', color:'#999'}}>${cartTotals.originalSubtotal.toFixed(2)}</span>
                  </div>
                  <div className="cart-row" style={{color:'#e53e3e'}}>
                    <span>{globalDiscount.label || t('single_meal.discount', '全單折扣')}</span>
                    <span>-${cartTotals.discount.toFixed(2)}</span>
                  </div>
                  <div className="cart-row">
                    <span>{t('meal_selection.discounted_subtotal', '折後小計')}</span>
                    <span>${cartTotals.subtotal.toFixed(2)}</span>
                  </div>
                </>
              )}
              {!discountEnabled && (
                <div className="cart-row">
                  <span>{t('meal_selection.subtotal')}</span>
                  <span>${cartTotals.subtotal.toFixed(2)}</span>
                </div>
              )}
              {orderType === 'delivery' && (
                <div className="cart-row fee-row">
                  <span>{t('meal_selection.delivery_fee')}</span>
                  <span style={{color: cartTotals.deliveryFee === 0 ? '#166534' : 'inherit'}}>
                    {cartTotals.deliveryFee === 0 ? t('meal_selection.free') : `$${cartTotals.deliveryFee.toFixed(2)}`}
                  </span>
                </div>
              )}
              <div className="cart-row tax-row">
                <span>GST (5%)</span>
                <span>${cartTotals.gst.toFixed(2)}</span>
              </div>
              <div className="cart-row tax-row">
                <span>PST (7%)</span>
                <span>${cartTotals.pst.toFixed(2)}</span>
              </div>
              <div className="cart-total-row">
                <span className="cart-total-label">{t('meal_selection.total_label')}</span>
                <span className="cart-total-amount">CAD$ {cartTotals.grandTotal.toFixed(2)}</span>
              </div>
            </div>
          )}

          <div className="cart-actions">
            <button
              className="checkout-btn"
              disabled={cartItems.length === 0 || (orderType === 'delivery' && !matchedZone)}
              onClick={handleCheckout}
            >
              {t('single_meal.checkout', '前往結帳')} <ChevronRight size={18} />
            </button>
            <button className="back-btn-cart" onClick={() => navigate(embedded ? '/' : '../')}>
              <ArrowLeft size={16} /> {t('single_meal.back', '返回')}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sticky cart bar + drawer */}
      {cartItems.length > 0 && (
        <>
          {/* Overlay */}
          {mobileCartOpen && (
            <div className="mobile-cart-overlay" onClick={() => setMobileCartOpen(false)} />
          )}

          {/* Drawer */}
          {mobileCartOpen && (
            <div className="mobile-cart-drawer">
              <div className="mobile-cart-drawer-handle" />
              <div className="mobile-cart-drawer-header">
                <span>{t('single_meal.cart_title', '購物車')} ({totalItemCount})</span>
                <button
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                  onClick={() => setMobileCartOpen(false)}
                >
                  <X size={20} color="#94a3b8" />
                </button>
              </div>
              <div className="mobile-cart-drawer-items">
                {cartItems.map(item => {
                  const modTotal = item.modifiers.reduce((s, m) => s + (m.price || 0), 0);
                  const lineOriginal = (item.price + modTotal) * item.quantity;
                  const lineTotal = lineOriginal * discountRate;
                  const menuItem = menuOptions.find(m => m.id === item.menuItemId);
                  const isSoldOut = menuItem && menuItem.is_available === false;
                  return (
                    <div key={item.cartId} className={`cart-item ${isSoldOut ? 'sold-out' : ''}`}>
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="cart-item-img" loading="lazy" decoding="async" />
                      ) : (
                        <div className="cart-item-img" style={{display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <ImageIcon size={18} color="#ccc" />
                        </div>
                      )}
                      <div className="cart-item-info">
                        <div className="cart-item-name">
                          {item.name}
                          {isSoldOut && <span className="cart-item-soldout-badge">售罄</span>}
                        </div>
                        {item.modifiers.length > 0 && (
                          <div className="cart-item-mods">
                            {item.modifiers.map((m, i) => (
                              <div key={i} className="cart-mod-line">
                                {m.name}
                                {m.price > 0 && (
                                  discountEnabled ? (
                                    <span style={{color:'#e53e3e', fontWeight:600}}> (+${(Number(m.price) * discountRate).toFixed(2)})</span>
                                  ) : (
                                    <span style={{color:'#ef4444', fontWeight:600}}> (+${Number(m.price).toFixed(2)})</span>
                                  )
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {item.note && (
                          <div className="cart-item-note">{t('single_meal.note_label', '備註')}: {item.note}</div>
                        )}
                        <div className="cart-item-bottom">
                          <div className="cart-item-price">
                            {discountEnabled && <span style={{textDecoration:'line-through', color:'#999', fontSize:'0.85em', marginRight:4}}>${lineOriginal.toFixed(2)}</span>}
                            ${lineTotal.toFixed(2)}
                          </div>
                          <div className="qty-control">
                            <button
                              className={`qty-btn ${item.quantity === 1 ? 'delete-btn' : ''}`}
                              onClick={() => updateCartItemQuantity(item.cartId, item.quantity - 1)}
                            >
                              {item.quantity === 1 ? <Trash2 size={14} /> : <Minus size={14} />}
                            </button>
                            <span className="qty-num">{item.quantity}</span>
                            <button
                              className="qty-btn"
                              onClick={() => updateCartItemQuantity(item.cartId, item.quantity + 1)}
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mobile-cart-drawer-totals">
                {discountEnabled && (
                  <>
                    <div className="cart-row">
                      <span>{t('meal_selection.subtotal')}</span>
                      <span style={{textDecoration:'line-through', color:'#999'}}>${cartTotals.originalSubtotal.toFixed(2)}</span>
                    </div>
                    <div className="cart-row" style={{color:'#e53e3e'}}>
                      <span>{globalDiscount.label || t('single_meal.discount', '全單折扣')}</span>
                      <span>-${cartTotals.discount.toFixed(2)}</span>
                    </div>
                    <div className="cart-row">
                      <span>{t('meal_selection.discounted_subtotal', '折後小計')}</span>
                      <span>${cartTotals.subtotal.toFixed(2)}</span>
                    </div>
                  </>
                )}
                {!discountEnabled && (
                  <div className="cart-row">
                    <span>{t('meal_selection.subtotal')}</span>
                    <span>${cartTotals.subtotal.toFixed(2)}</span>
                  </div>
                )}
                {orderType === 'delivery' && (
                  <div className="cart-row fee-row">
                    <span>{t('meal_selection.delivery_fee')}</span>
                    <span style={{color: cartTotals.deliveryFee === 0 ? '#166534' : 'inherit'}}>
                      {cartTotals.deliveryFee === 0 ? t('meal_selection.free') : `$${cartTotals.deliveryFee.toFixed(2)}`}
                    </span>
                  </div>
                )}
                <div className="cart-row tax-row"><span>GST (5%)</span><span>${cartTotals.gst.toFixed(2)}</span></div>
                <div className="cart-row tax-row"><span>PST (7%)</span><span>${cartTotals.pst.toFixed(2)}</span></div>
                <div className="cart-total-row">
                  <span className="cart-total-label">{t('meal_selection.total_label')}</span>
                  <span className="cart-total-amount">CAD$ {cartTotals.grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Bottom bar */}
          <div className="mobile-cart-bar">
            <div className="mobile-cart-bar-info" onClick={() => setMobileCartOpen(prev => !prev)}>
              <div className="mobile-cart-bar-badge">
                <ShoppingCart size={20} />
                <span className="mobile-cart-bar-badge-count">{totalItemCount}</span>
              </div>
              <div className="mobile-cart-bar-text">
                <div className="mobile-cart-bar-label">{t('single_meal.view_cart', '查看購物車')}</div>
                <div className="mobile-cart-bar-total">CAD$ {cartTotals.grandTotal.toFixed(2)}</div>
              </div>
              <ChevronDown size={18} className={`mobile-cart-bar-arrow ${mobileCartOpen ? 'open' : ''}`} />
            </div>
            <button
              className="mobile-cart-bar-btn"
              onClick={handleCheckout}
            >
              {t('single_meal.checkout', '結帳')} <ChevronRight size={16} />
            </button>
          </div>
        </>
      )}

      {/* Modifier Modal */}
      {modModalDish && (
        <div className="mod-overlay">
          <div className="mod-modal">
            <div className="mod-modal-header">
              <button className="mod-back-btn" onClick={() => setModModalDish(null)}>
                <ArrowLeft size={18} />
                {t('meal_selection.back_to_menu', '返回菜單')}
              </button>
            </div>

            <div className="mod-modal-body">
              <div className="mod-dish-info">
                {modModalDish.image_url && (
                  <img src={modModalDish.image_url} alt={modModalDish.name} className="mod-dish-img" loading="eager" decoding="async" />
                )}
                <div className="mod-dish-detail">
                  <div className="mod-dish-name">{modModalDish.name}</div>
                  <div className="mod-dish-price-label">
                    {discountEnabled ? (
                      <>
                        <span style={{textDecoration:'line-through', color:'#999', fontSize:'0.85em', marginRight:6}}>CAD$ {modModalDish.price}</span>
                        <span style={{color:'#e53e3e'}}>CAD$ {(modModalDish.price * discountRate).toFixed(2)}</span>
                      </>
                    ) : (
                      <>CAD$ {modModalDish.price}</>
                    )}
                  </div>
                  {modModalDish.description && (
                    <div className="mod-dish-desc">{modModalDish.description}</div>
                  )}
                </div>
              </div>

              {getDishModifierGroups(modModalDish).map(group => (
                <div key={group.id} className="mod-group">
                  <div className="mod-group-head">
                    <span className="mod-group-name">{group.name}</span>
                    <span className={`mod-rules-badge ${group.min_required > 0 ? 'badge-required' : 'badge-optional'}`}>
                      {group.min_required > 0 ? t('meal_selection.required') : t('meal_selection.optional')}
                      {` (${t('meal_selection.max_select', { max: group.max_allowed })})`}
                    </span>
                  </div>
                  <div className="mod-options">
                    {group.modifiers.map(mod => {
                      const isSelected = (selectedModifiers[group.id] || []).some(m => m.id === mod.id);
                      return (
                        <div
                          key={mod.id}
                          className={`mod-opt ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleModifier(group, mod)}
                        >
                          <div className="mod-opt-left">
                            <span className="mod-opt-name">{mod.name}</span>
                            <span className="mod-opt-price">
                              {mod.price > 0 ? (
                                discountEnabled ? (
                                  <>
                                    <span style={{textDecoration:'line-through', color:'#999', marginRight:4}}>+${mod.price}</span>
                                    <span style={{color:'#e53e3e'}}>+${(mod.price * discountRate).toFixed(2)}</span>
                                  </>
                                ) : `+$${mod.price}`
                              ) : t('meal_selection.free')}
                            </span>
                          </div>
                          <div className="mod-opt-radio">
                            <div className="mod-opt-radio-inner"></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="mod-note-section">
                <label className="mod-note-label">{t('single_meal.item_note', '備註')}</label>
                <textarea
                  className="mod-note-input"
                  placeholder={t('single_meal.item_note_placeholder', '例：少辣、不要蔥、去冰...')}
                  value={itemNote}
                  onChange={e => setItemNote(e.target.value)}
                  maxLength={200}
                />
              </div>
            </div>

            <div className="mod-modal-footer">
              <div className="mod-total-wrapper">
                <span className="mod-total-label">{t('meal_selection.estimated')}</span>
                <span className="mod-total-amount">CAD$ {calculateModPreviewTotal()}</span>
              </div>
              <button className="mod-confirm-btn" onClick={handleConfirmModifiers}>
                {t('single_meal.add_to_cart', '加入購物車')} <Check size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SingleMealSelection;
